import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
    Box, Typography, Card, CardContent, Grid, CircularProgress, Alert, LinearProgress, Button, Pagination, TextField, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
    IconButton, FormControlLabel, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar
} from '@mui/material';
import {
    Refresh, Search, Clear, QrCode as QrCodeIcon, ContentCopy as CopyIcon
} from '@mui/icons-material';

// Helper function to format business data - ensure no objects are rendered
const formatBusinessData = (business) => {
    // Handle address - convert object to string if needed
    let addressStr = 'N/A';
    if (business.address) {
        if (typeof business.address === 'string') {
            addressStr = business.address;
        } else if (business.address.formattedAddress) {
            addressStr = business.address.formattedAddress;
        } else if (business.address.street || business.address.city) {
            addressStr = [business.address.street, business.address.city, business.address.state, business.address.country]
                .filter(Boolean)
                .join(', ');
        }
    }

    // Safely get array lengths and filter results
    const posts = Array.isArray(business.posts) ? business.posts : [];
    const keywords = Array.isArray(business.keywords) ? business.keywords : [];
    const reviews = Array.isArray(business.reviews) ? business.reviews : [];
    const rankingSnapshots = Array.isArray(business.rankingSnapshots) ? business.rankingSnapshots : [];

    // Debug: Log all received fields
    if (business.businessName) {
        console.log(`🔍 formatBusinessData for ${business.businessName}:`, {
            hasCustomAiPrompt: !!business.customAiPrompt,
            customAiPromptLength: business.customAiPrompt?.length || 0,
            customAiPromptValue: business.customAiPrompt || 'NOT SET',
            allKeys: Object.keys(business)
        });
    }

    return {
        id: business._id || business.id,
        businessName: business.businessName || 'N/A',
        locationId: business.googleLocationId || business.locationId || 'N/A',
        placeId: business.placeId || 'N/A',
        realPlaceId: business.realPlaceId || 'N/A',
        lat: business.lat || 'N/A',
        lng: business.lng || 'N/A',
        address: addressStr,
        addressObject: business.addressObject || business.address || {}, // Use API's addressObject if available, fallback to address
        rankingSnapshotLength: rankingSnapshots.length || 0,
        keywordsCount: keywords.length || 0,
        totalPosts: posts.length || 0,
        scheduledPosts: posts.filter(p => p?.status === 'scheduled').length || 0,
        publishedPosts: posts.filter(p => p?.status === 'published').length || 0,
        reviewsCount: reviews.length || 0,
        createdAt: business.createdAt || 'N/A',
        phoneNumber: business.phoneNumber || '',
        websiteUrl: business.websiteUrl || '',
        email: business.email || '',
        contactCloudTenantId: business.contactCloudTenantId || '',
        userId: business.userId || 'N/A',
        teamId: business.teamId || 'N/A',
        reportSent: business.reportSent || false,
        reportSentAt: business.reportSentAt || 'N/A',
        qrCodeUrl: business.qrCodeUrl || null,
        hasQrCode: !!(business.qrCodeUrl && business.qrCodeUrl.trim()),
        customAiPrompt: business.customAiPrompt || '',
        // Debug: Log QR data for first few businesses
        ...(console.log(`🔍 Business ${business.businessName}: hasQrCode=${!!(business.qrCodeUrl && business.qrCodeUrl.trim())}, qrUrl=${business.qrCodeUrl || 'NULL'}`), {})
    };
};

// In-memory cache for all businesses data (no localStorage quota issues)
const apiCache = new Map();

const AllBusinessesPage = () => {
    // Hooks
    const navigate = useNavigate();

    console.log('🔄 AllBusinessesPage component mounted');

    // State
    const [loading, setLoading] = useState(false);
    const [businessesData, setBusinessesData] = useState([]);
    const [error, setError] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('Lade Unternehmensdaten...');
    const [progress, setProgress] = useState({ percentage: 0, text: '0%', processed: 0, total: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20); // 20 items per page for better viewing
    const [searchTerm, setSearchTerm] = useState('');
    const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false); // Track if we've tried to load data
    const [showNoEmail, setShowNoEmail] = useState(false); // Toggle to show businesses without email
    const [showReportSentFalse, setShowReportSentFalse] = useState(false); // Toggle to show where reportSent is false
    const [showNoCloudTenantId, setShowNoCloudTenantId] = useState(false); // Toggle to show businesses without Cloud Tenant ID
    const [editingEmailId, setEditingEmailId] = useState(null); // Track which business is being edited
    const [editEmailValue, setEditEmailValue] = useState(''); // Store the email being edited
    const [editingCloudTenantId, setEditingCloudTenantId] = useState(null); // Track which business Cloud Tenant ID is being edited
    const [editCloudTenantValue, setEditCloudTenantValue] = useState(''); // Store the Cloud Tenant ID being edited
    const [editingAddressId, setEditingAddressId] = useState(null); // Track which business address is being edited
    const [editAddressData, setEditAddressData] = useState({
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'DE',
        formattedAddress: '',
        lat: '',
        lng: ''
    }); // Store the address being edited
    const [confirmDialog, setConfirmDialog] = useState(false); // Confirmation dialog state
    const [pendingSave, setPendingSave] = useState(null); // Store pending save data { locationId, businessName, email/cloudTenantId/address, fieldType }
    const [qrModal, setQrModal] = useState({ open: false, business: null }); // QR code modal state
    const [customPromptModal, setCustomPromptModal] = useState({ open: false, business: null }); // Custom prompt modal state
    const [copySuccess, setCopySuccess] = useState(false); // Copy success notification
    const [creatingQr, setCreatingQr] = useState(null); // Track QR creation progress (locationId)

    const abortControllerRef = useRef(null);

    // Create QR code for business
    const createQrCode = async (business) => {
        setCreatingQr(business.locationId);
        
        try {
            console.log('🎨 Creating QR code for:', business.businessName);
            
            const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
            console.log('🔑 Token check:', { 
                auth_token: !!localStorage.getItem('auth_token'),
                token: !!localStorage.getItem('token'),
                finalToken: !!token,
                tokenLength: token?.length
            });
            
            const response = await fetch(`/api/businesses/${business.locationId}/create-qr`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('✅ QR code created:', result.qrCodeUrl);
                
                // Update the business in local state
                setBusinessesData(prevData => 
                    prevData.map(b => 
                        b.locationId === business.locationId 
                            ? { ...b, qrCodeUrl: result.qrCodeUrl, hasQrCode: true }
                            : b
                    )
                );
                
                // Show success message
                setCopySuccess(true);
                
            } else {
                const error = await response.text();
                console.error('❌ Failed to create QR code:', error);
                alert('Fehler beim Erstellen des QR Codes: ' + error);
            }
        } catch (error) {
            console.error('❌ QR creation error:', error);
            alert('Fehler beim Erstellen des QR Codes: ' + error.message);
        } finally {
            setCreatingQr(null);
        }
    };

    // Load all businesses with streaming
    const loadAllBusinesses = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        setHasAttemptedLoad(true);
        setError(null);

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        console.log('🔍 Available tokens in localStorage:', {
            auth_token: !!localStorage.getItem('auth_token'),
            token: !!localStorage.getItem('token'),
            keys: Object.keys(localStorage)
        });

        if (!token) {
            setError('Authentifizierung erforderlich. Bitte melden Sie sich an.');
            setLoading(false);
            return;
        }

        // Check in-memory cache if not forcing refresh (instant, no quota issues)
        if (!forceRefresh && apiCache.has('allBusinessesCache')) {
            const cachedData = apiCache.get('allBusinessesCache');
            setBusinessesData(cachedData);
            setProgress({
                percentage: 100,
                text: `100% - ${cachedData.length} Unternehmen (aus Cache)`,
                processed: cachedData.length,
                total: cachedData.length
            });
            setLoadingMessage(`✅ ${cachedData.length} Unternehmen aus Cache geladen`);
            setTimeout(() => setLoading(false), 500);
            return;
        } else if (forceRefresh) {
            // Clear cache when forcing refresh
            apiCache.delete('allBusinessesCache');
        }

        setBusinessesData([]);
        setProgress({ percentage: 0, text: '0%', processed: 0, total: 0 });
        setCurrentPage(1);

        try {
            setLoadingMessage('Verbinde mit API und starte Datenstrom...');
            console.log(`📡 Streaming from /api/all-businesses`);
            console.log(`🔑 Using token: ${token ? token.substring(0, 20) + '...' : 'NO TOKEN'}`);
            console.log(`🌍 API URL: ${window.location.origin}/api/all-businesses`);
            console.log(`📍 Environment: ${process.env.NODE_ENV || 'production'}`);

            // Call only /api/all-businesses-json
            console.log('📡 Calling /api/all-businesses-json');
            const response = await fetch(`/api/all-businesses-json`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                signal: abortControllerRef.current.signal,
                credentials: 'same-origin'
            });

            console.log(`📡 Response: ${response.status} ${response.statusText}`);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ API Error: ${response.status}`, errorText);
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            // Parse JSON response
            setLoadingMessage('Verarbeite JSON-Daten...');
            const jsonData = await response.json();
            console.log('✅ Data received:', jsonData);

            if (jsonData.success && jsonData.data) {
                // Format all business data
                const formattedData = jsonData.data.map(business => formatBusinessData(business));
                setBusinessesData(formattedData);

                // Cache in memory (instant, no quota issues)
                apiCache.set('allBusinessesCache', formattedData);

                setProgress({
                    percentage: 100,
                    text: `100% - ${jsonData.count} Unternehmen`,
                    processed: jsonData.count,
                    total: jsonData.count
                });
                setLoadingMessage(`✅ ${jsonData.count} Unternehmen geladen`);

                setTimeout(() => {
                    setLoading(false);
                }, 2000);
            } else {
                throw new Error(jsonData.error || 'Fehler beim Laden der Daten');
            }

        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('❌ Error loading businesses:', err);
                setError(`Fehler beim Laden: ${err.message}`);
                setLoading(false);
            }
        }
    }, []);

    // Auto-load all businesses on component mount
    useEffect(() => {
        console.log('🔄 AllBusinessesPage useEffect triggered - loading data');
        loadAllBusinesses();
    }, [loadAllBusinesses]);

    // Debug logging when data changes
    useEffect(() => {
        console.log('📊 businessesData updated:', {
            length: businessesData.length,
            loading,
            error,
            data: businessesData.slice(0, 2) // Show first 2 items for debugging
        });
    }, [businessesData, loading, error]);

    // Load custom AI prompt when modal opens - fetch from DB if not in business object
    useEffect(() => {
        if (customPromptModal.open && customPromptModal.business) {
            const business = customPromptModal.business;
            // Check if customAiPrompt is already in business object
            if (!business.customAiPrompt) {
                // Fetch from DB if not available
                const fetchCustomPrompt = async () => {
                    try {
                        const response = await fetch(
                            `/api/business/${business.locationId || business.id}/custom-ai-prompt`,
                            {
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                            }
                        );
                        if (response.ok) {
                            const responseData = await response.json();
                            const customPrompt = responseData.data?.customAiPrompt || '';
                            // Update the business object in the modal with the fetched customAiPrompt
                            setCustomPromptModal(prev => ({
                                ...prev,
                                business: { ...prev.business, customAiPrompt: customPrompt }
                            }));
                            console.log('✅ Loaded custom AI prompt from DB for modal:', customPrompt?.substring(0, 50) || '(empty)');
                        }
                    } catch (error) {
                        console.warn('⚠️ Failed to fetch custom AI prompt:', error);
                    }
                };
                fetchCustomPrompt();
            }
        }
    }, [customPromptModal.open]);

    // Handle email editing
    const handleEmailEdit = (locationId, currentEmail) => {
        setEditingEmailId(locationId);
        setEditEmailValue(currentEmail || '');
    };

    const handleEmailSave = (locationId, businessName) => {
        if (!editEmailValue.trim()) {
            alert('E-Mail darf nicht leer sein');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editEmailValue.trim())) {
            alert('Bitte geben Sie eine gültige E-Mail-Adresse ein');
            return;
        }

        // Show confirmation dialog instead of saving directly
        setPendingSave({
            locationId,
            businessName,
            email: editEmailValue.trim()
        });
        setConfirmDialog(true);
    };

    const handleEmailConfirm = async () => {
        if (!pendingSave) return;

        const { locationId, businessName, email, value, fieldType } = pendingSave;
        const isEmail = fieldType === 'email' || email;
        const apiEndpoint = isEmail ? 'email' : 'cloudTenantId';
        const dataToSend = isEmail ? { email } : { contactCloudTenantId: value };
        const fieldName = isEmail ? 'E-Mail' : 'Cloud Tenant ID';

        try {
            const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
            const response = await fetch(`/api/business/${locationId}/${apiEndpoint}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(dataToSend)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Fehler beim Speichern');
            }

            // Update local state
            const updatedBusinesses = businessesData.map(b => {
                if (b.locationId === locationId) {
                    if (isEmail) {
                        return { ...b, email };
                    } else {
                        return { ...b, contactCloudTenantId: value };
                    }
                }
                return b;
            });
            setBusinessesData(updatedBusinesses);

            // Reset states based on field type
            if (isEmail) {
                setEditingEmailId(null);
                setEditEmailValue('');
            } else {
                setEditingCloudTenantId(null);
                setEditCloudTenantValue('');
            }

            setConfirmDialog(false);
            setPendingSave(null);
            alert(`✅ ${fieldName} für ${businessName} erfolgreich aktualisiert`);
        } catch (error) {
            console.error(`❌ Error updating ${fieldName}:`, error);
            alert(`Fehler: ${error.message}`);
            setConfirmDialog(false);
            setPendingSave(null);
        }
    };

    const handleEmailConfirmCancel = () => {
        setConfirmDialog(false);
        setPendingSave(null);
    };

    const handleEmailCancel = () => {
        setEditingEmailId(null);
        setEditEmailValue('');
    };

    // Handle Cloud Tenant ID editing
    const handleCloudTenantEdit = (locationId, currentValue) => {
        setEditingCloudTenantId(locationId);
        setEditCloudTenantValue(currentValue || '');
    };

    const handleCloudTenantSave = (locationId, businessName) => {
        // Show confirmation dialog instead of saving directly
        setPendingSave({
            locationId,
            businessName,
            value: editCloudTenantValue.trim(),
            fieldType: 'cloudTenantId'
        });
        setConfirmDialog(true);
    };

    const handleCloudTenantCancel = () => {
        setEditingCloudTenantId(null);
        setEditCloudTenantValue('');
    };

    // Handle address editing
    const handleAddressEdit = (locationId, currentAddress, currentLat, currentLng) => {
        // Parse address if it's a string or object
        let addressObj = {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'DE',
            formattedAddress: '',
            lat: currentLat || '',
            lng: currentLng || ''
        };

        if (currentAddress) {
            if (typeof currentAddress === 'string') {
                addressObj.formattedAddress = currentAddress;
            } else if (typeof currentAddress === 'object') {
                addressObj = {
                    street: currentAddress.street || '',
                    city: currentAddress.city || '',
                    state: currentAddress.state || '',
                    postalCode: currentAddress.postalCode || '',
                    country: currentAddress.country || 'DE',
                    formattedAddress: currentAddress.formattedAddress || '',
                    lat: currentLat || '',
                    lng: currentLng || ''
                };
            }
        }

        setEditingAddressId(locationId);
        setEditAddressData(addressObj);
    };

    const handleAddressDataChange = (field, value) => {
        setEditAddressData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddressSave = (locationId, businessName) => {
        // Validate at least city or formattedAddress
        if (!editAddressData.city && !editAddressData.formattedAddress) {
            alert('Bitte geben Sie mindestens die Stadt oder die vollständige Adresse ein');
            return;
        }

        // Validate coordinates if provided
        if (editAddressData.lat || editAddressData.lng) {
            const lat = parseFloat(editAddressData.lat);
            const lng = parseFloat(editAddressData.lng);

            if (isNaN(lat) || isNaN(lng)) {
                alert('Bitte geben Sie gültige Koordinaten ein (Zahlen)');
                return;
            }

            if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                alert('Ungültige Koordinaten: Breitengrad -90 bis 90, Längengrad -180 bis 180');
                return;
            }
        }

        // Show confirmation dialog
        setPendingSave({
            locationId,
            businessName,
            address: editAddressData,
            fieldType: 'address'
        });
        setConfirmDialog(true);
    };

    const handleAddressConfirm = async () => {
        if (!pendingSave) return;

        const { locationId, businessName, address, fieldType } = pendingSave;

        try {
            const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
            const response = await fetch(`/api/business/${locationId}/address`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    address: {
                        street: address.street.trim(),
                        city: address.city.trim(),
                        state: address.state.trim(),
                        postalCode: address.postalCode.trim(),
                        country: address.country.trim(),
                        formattedAddress: address.formattedAddress.trim()
                    },
                    lat: address.lat ? parseFloat(address.lat) : undefined,
                    lng: address.lng ? parseFloat(address.lng) : undefined
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Fehler beim Speichern');
            }

            // Update local state
            const updatedBusinesses = businessesData.map(b => {
                if (b.locationId === locationId) {
                    const addressObj = {
                        street: address.street.trim(),
                        city: address.city.trim(),
                        state: address.state.trim(),
                        postalCode: address.postalCode.trim(),
                        country: address.country.trim(),
                        formattedAddress: address.formattedAddress.trim()
                    };

                    return {
                        ...b,
                        address: address.formattedAddress || `${address.street}, ${address.city}, ${address.state}, ${address.postalCode}, ${address.country}`.trim(),
                        addressObject: addressObj,
                        lat: address.lat ? parseFloat(address.lat) : b.lat,
                        lng: address.lng ? parseFloat(address.lng) : b.lng
                    };
                }
                return b;
            });
            setBusinessesData(updatedBusinesses);

            // Reset states
            setEditingAddressId(null);
            setEditAddressData({
                street: '',
                city: '',
                state: '',
                postalCode: '',
                country: 'DE',
                formattedAddress: '',
                lat: '',
                lng: ''
            });

            setConfirmDialog(false);
            setPendingSave(null);
            alert(`✅ Adresse für ${businessName} erfolgreich aktualisiert`);

            // Refetch data to ensure all changes are synced
            setTimeout(() => {
                loadAllBusinesses();
            }, 500);
        } catch (error) {
            console.error('❌ Error updating address:', error);
            alert(`Fehler: ${error.message}`);
            setConfirmDialog(false);
            setPendingSave(null);
        }
    };

    const handleAddressCancel = () => {
        setEditingAddressId(null);
        setEditAddressData({
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'DE',
            formattedAddress: '',
            lat: '',
            lng: ''
        });
    };

    // Filtered and paginated data
    const filteredData = useMemo(() => {
        let result = businessesData;

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(business => {
                try {
                    return (
                        (business?.businessName && String(business.businessName).toLowerCase().includes(term)) ||
                        (business?.locationId && String(business.locationId).toLowerCase().includes(term)) ||
                        (business?.address && String(business.address).toLowerCase().includes(term)) ||
                        (business?.placeId && String(business.placeId).toLowerCase().includes(term)) ||
                        (business?.phoneNumber && String(business.phoneNumber).toLowerCase().includes(term)) ||
                        (business?.email && String(business.email).toLowerCase().includes(term)) ||
                        (business?.userId && String(business.userId).toLowerCase().includes(term))
                    );
                } catch (err) {
                    console.warn('Error filtering business:', business, err);
                    return false;
                }
            });
        }

        // Apply no email filter
        if (showNoEmail) {
            result = result.filter(business => !business?.email || business.email.trim() === '');
        }

        // Apply reportSent false filter
        if (showReportSentFalse) {
            result = result.filter(business => business?.reportSent === false);
        }

        // Apply no Cloud Tenant ID filter
        if (showNoCloudTenantId) {
            result = result.filter(business => !business?.contactCloudTenantId || business.contactCloudTenantId.trim() === '');
        }

        return result;
    }, [businessesData, searchTerm, showNoEmail, showReportSentFalse, showNoCloudTenantId]);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    // Handle page change
    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    // Handle search
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Format date
    const formatDate = (dateString) => {
        if (dateString === 'N/A') return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('de-DE');
        } catch {
            return 'N/A';
        }
    };

    return (
    <Box sx={{ p: 3, background:  'primary' }}>
        {/* Header */}
        <Card sx={{ 
            mb: 3, 
            background: 'primary', 
            color: 'primary',
            boxShadow: '0 8px 32px rgba(25, 118, 210, 0.2)',
            border: 'none',
            borderRadius: 3
        }}>
            <CardContent sx={{ p: 3 }}>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={6}>
<Typography
              variant="h4"
              sx={{
                mb: 0.5,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: amDesignColors.primary,
                fontFamily: amDesignTypography.fontFamily.heading
              }}
            >  Portfolio</Typography>
                       


                        <Typography variant="h1" fontWeight="bold" sx={{ mb: 1 }}>
                             Alle Unternehmen
                        </Typography>
                        <Typography
              variant="body2"
              sx={{
                color: amDesignColors.text.secondary,
                fontFamily: amDesignTypography.fontFamily.body
              }}
            >
                           {filteredData.length} Geschäftsprofile · zentral verwaltet
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 2 }}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                apiCache.delete('allBusinessesCache');
                                loadAllBusinesses(true);
                                alert('Cache geleert und Daten aktualisiert');
                            }}
                            disabled={loading}
                            startIcon={<Refresh />}
                            sx={{
                                background: amDesignColors.warning,
                                color: amDesignColors.white,
                                fontWeight: 'bold',
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                fontSize: '0.85rem',
                                '&:hover': {
                                    background: '#f57c00',
                                    boxShadow: '0 6px 20px rgba(255, 152, 0, 0.4)',
                                    transform: 'translateY(-1px)'
                                },
                                '&:disabled': {
                                    background: amDesignColors.border,
                                    color: amDesignColors.text.tertiary
                                }
                            }}
                        >
                            Cache leeren
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => loadAllBusinesses(true)}
                            disabled={loading}
                            startIcon={<Refresh />}
                            sx={{
                                background: 'primary',
                                color: 'primary',
                                fontWeight: 'bold',
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 15px rgba(255, 255, 255, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(45deg, #f5f5f5 30%, amDesignColors.border 90%)',
                                    boxShadow: '0 6px 20px rgba(255, 255, 255, 0.4)',
                                    transform: 'translateY(-1px)'
                                },
                                '&:disabled': {
                                    background: amDesignColors.border,
                                    color: amDesignColors.text.tertiary
                                }
                            }}
                        >
                            {loading ? 'Lädt...' : 'Aktualisieren'}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>

        {/* Loading Progress */}
        {loading && (
            <Card sx={{ 
                mb: 3, 
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: '1px solid amDesignColors.border'
            }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CircularProgress size={20} sx={{ mr: 2, color: amDesignColors.primary }} />
                        <Typography variant="body1" fontWeight="medium">
                            {loadingMessage}
                        </Typography>
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={progress.percentage} 
                        sx={{ 
                            mb: 1, 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: amDesignColors.border,
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: 'linear-gradient(45deg, #19d228ff 30%, #42a5f5 90%)'
                            }
                        }} 
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                        {progress.text} ({progress.processed}/{progress.total})
                    </Typography>
                </CardContent>
            </Card>
        )}

        {/* Error Alert */}
        {error && (
            <Alert 
                severity="error" 
                onClose={() => setError(null)} 
                sx={{ 
                    mb: 3, 
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(211, 47, 47, 0.1)',
                    '& .MuiAlert-message': {
                        fontWeight: 'medium'
                    }
                }}
            >
                {error}
            </Alert>
        )}

        {/* Search Bar */}
        <Card sx={{
            mb: 3,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid amDesignColors.border',
            background: amDesignColors.white
        }}>
            <CardContent sx={{ p: 2.5 }}>
                <TextField
                    fullWidth
                    placeholder="Nach Unternehmensname, Location ID, Adresse, Place ID, Telefon, E-Mail oder User ID suchen..."
                    value={searchTerm}
                    onChange={handleSearch}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: amDesignColors.paper,
                            '&:hover': {
                                backgroundColor: '#f5f5f5'
                            },
                            '&.Mui-focused': {
                                backgroundColor: amDesignColors.white,
                                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                            }
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search sx={{ color: '#54d219ff' }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleClearSearch}
                                    sx={{
                                        color: amDesignColors.text.secondary,
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                            color: amDesignColors.primary
                                        }
                                    }}
                                >
                                    <Clear />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </CardContent>
        </Card>

       

        {/* Data Table */}
        {!loading && businessesData.length > 0 && (
            <>
                {/* Table Info Card */}
                <Card sx={{ 
                    mb: 2, 
                    background: 'primary',
                    borderRadius: 3,
                    boxShadow: '0 4px 15px rgba(33, 150, 243, 0.15)',
                    border: 'none'
                }}>
                    <CardContent sx={{ py: 2, px: 3 }}>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary' }}>
                                     Zeige {startIndex + 1} bis {Math.min(startIndex + itemsPerPage, filteredData.length)} von {filteredData.length} Unternehmen
                                    {searchTerm && (
                                        <Typography component="span" variant="body2" sx={{ color: amDesignColors.text.secondary, ml: 1 }}>
                                            (gefiltert aus {businessesData.length} insgesamt)
                                        </Typography>
                                    )}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>





 {/* Toggle Filters */}
       
            <CardContent sx={{ p: 0.5 }}>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showNoCloudTenantId}
                                    onChange={(e) => {
                                        setShowNoCloudTenantId(e.target.checked);
                                        setCurrentPage(1); // Reset to first page
                                    }}
                                    color="primary"
                                />
                            }
                            label="Keine Cloud Tenant ID"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showNoEmail}
                                    onChange={(e) => {
                                        setShowNoEmail(e.target.checked);
                                        setCurrentPage(1); // Reset to first page
                                    }}
                                    color="primary"
                                />
                            }
                            label="Keine E-Mail"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showReportSentFalse}
                                    onChange={(e) => {
                                        setShowReportSentFalse(e.target.checked);
                                        setCurrentPage(1); // Reset to first page
                                    }}
                                    color="primary"
                                />
                            }
                            label="Keine Report"
                        />
                    </Grid>
                </Grid>
            </CardContent>
       



                                <Chip
                                    label={`Gesamt: ${businessesData.length}`}
                                    size="small"
                                    color="primary"
                                    variant="filled"
                                    sx={{ fontWeight: 'bold', background: 'primary' }}
                                />
                                <Chip
                                    label={`Seite ${currentPage} von ${totalPages}`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Scrollable Table Container */}
                <Card sx={{ 
                    mb: 3, 
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid amDesignColors.border',
                    overflow: 'hidden'
                }}>
                    <TableContainer
                        sx={{
                            maxHeight: 'calc(100vh - 300px)',
                            '&::-webkit-scrollbar': {
                                height: '12px',
                                width: '12px'
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '6px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'linear-gradient(45deg, amDesignColors.primary 30%, #42a5f5 90%)',
                                borderRadius: '6px',
                                border: '2px solid #f1f1f1'
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: 'linear-gradient(45deg, #1565c0 30%, amDesignColors.primary 90%)'
                            }
                        }}
                    >
                        <Table
                            sx={{
                                minWidth: 2400,
                                width: '100%',
                                '& .MuiTableCell-root': {
                                    whiteSpace: 'nowrap',
                                    padding: '12px 8px',
                                    fontSize: '0.8rem',
                                    borderRight: '1px solid #e8e8e8',
                                    borderBottom: '1px solid #e8e8e8'
                                }
                            }}
                        >
                            <TableHead>
                                <TableRow sx={{ 
                                    '& .MuiTableCell-head': { 
                                        fontWeight: 'bold', 
                                        fontSize: '0.8rem', 
                                        background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                                        borderRight: '1px solid amDesignColors.border',
                                        borderBottom: '2px solid amDesignColors.border',
                                        color: '#333',
                                        padding: '14px 8px'
                                    } 
                                }}>
                                     <TableCell sx={{ minWidth: 200 }}> ID</TableCell>
                                    <TableCell sx={{ minWidth: 380 }}> Unternehmensname</TableCell>
                                    <TableCell sx={{ minWidth: 160 }}> Location ID</TableCell>
                                    <TableCell sx={{ minWidth: 250 }}>Place ID</TableCell>
                                    <TableCell sx={{ minWidth: 250 }}>E-Mail</TableCell>
                                    <TableCell sx={{ minWidth: 250 }}> Real Place ID</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 150 }}> Koordinaten</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>Adresse</TableCell>
                                    <TableCell sx={{ minWidth: 150 }}>Telefon</TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>Website</TableCell>
                                    
                                    <TableCell sx={{ minWidth: 250 }}>Cloud Tenant ID</TableCell>
                                    <TableCell sx={{ minWidth: 220 }}> User ID</TableCell>
                                    <TableCell sx={{ minWidth: 220 }}> Team ID</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 110 }}> Erstellt</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 140 }}> Report Sent</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 120 }}> QR Code</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 150 }}> KI-Anweisungen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.map((business, index) => (
                                    <TableRow
                                        key={`${business.locationId}-${index}`} 
                                        hover
                                        sx={{
                                            '&:hover': { 
                                                backgroundColor: '#f8fbff',
                                                transform: 'scale(1)',
                                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.1)'
                                            },
                                            '&:nth-of-type(even)': { 
                                                backgroundColor: amDesignColors.paper 
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    > 
                                         <TableCell sx={{ 
                                            fontWeight: 700, 
                                            color: '#b11313ff', 
                                            fontSize: '0.85rem', 
                                            maxWidth: 50, 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis',
                                            background: index % 2 === 0 ? amDesignColors.white : amDesignColors.paper
                                        }}>
                                            {business.id}
                                        </TableCell>

                                        <TableCell sx={{ 
                                            fontWeight: 700, 
                                            color: amDesignColors.primary, 
                                            fontSize: '0.85rem', 
                                            maxWidth: 200, 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis',
                                            background: index % 2 === 0 ? amDesignColors.white : amDesignColors.paper
                                        }}>
                                            {business.businessName}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontSize: '0.78rem', 
                                            color: amDesignColors.text.secondary, 
                                            fontFamily: "'JetBrains Mono', monospace", 
                                            maxWidth: 180, 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis',
                                            background: 'transparent'
                                        }}>
                                            {business.locationId !== 'N/A' ? business.locationId : '-'}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontSize: '0.78rem', 
                                            color: 'amDesignColors.text.secondary666ff', 
                                            fontFamily: "'JetBrains Mono', monospace", 
                                            maxWidth: 160, 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis' 
                                        }}>
                                            {business.placeId !== 'N/A' ? business.placeId : '-'}
                                        </TableCell>

                                        <TableCell sx={{
                                            fontSize: '0.8rem',
                                            color: amDesignColors.primary,
                                            maxWidth: 'none',
                                            overflow: 'visible',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {editingEmailId === business.locationId ? (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <TextField
                                                        size="small"
                                                        value={editEmailValue}
                                                        onChange={(e) => setEditEmailValue(e.target.value)}
                                                        placeholder="Email eingeben"
                                                        sx={{ width: '200px' }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="success"
                                                        onClick={() => handleEmailSave(business.locationId, business.businessName)}
                                                    >
                                                        Speichern
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={handleEmailCancel}
                                                    >
                                                        Abbrechen
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    {business.email && business.email !== 'N/A' ? (
                                                        <a
                                                            href={`mailto:${business.email}`}
                                                            style={{
                                                                color: amDesignColors.primary,
                                                                textDecoration: 'none',
                                                                fontWeight: '500'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.target.style.color = '#1565c0';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.target.style.color = amDesignColors.primary;
                                                            }}
                                                        >
                                                            {business.email}
                                                        </a>
                                                    ) : (
                                                        <span style={{ color: '#999' }}>-</span>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={() => handleEmailEdit(business.locationId, business.email)}
                                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                                    >
                                                        ✏️
                                                    </Button>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontSize: '0.78rem', 
                                            color: amDesignColors.text.secondary, 
                                            fontFamily: "'JetBrains Mono', monospace", 
                                            maxWidth: 160, 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis' 
                                        }}>
                                            {business.realPlaceId !== 'N/A' ? business.realPlaceId : '-'}
                                        </TableCell>
                                        <TableCell align="center" sx={{ 
                                            fontSize: '0.75rem', 
                                            color: '#2e7d32', 
                                            fontFamily: "'JetBrains Mono', monospace",
                                            fontWeight: 500
                                        }}>
                                            {business.lat !== 'N/A' && business.lng !== 'N/A'
                                                ? `${parseFloat(business.lat).toFixed(4)}, ${parseFloat(business.lng).toFixed(4)}`
                                                : 'N/A'
                                            }
                                        </TableCell>
                                        <TableCell sx={{
                                            fontSize: '0.8rem',
                                            color: '#555',
                                            maxWidth: 450,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {business.address && business.address !== 'N/A' ? business.address : '-'}
                                                </span>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => handleAddressEdit(business.locationId, business.addressObject, business.lat, business.lng)}
                                                    sx={{ minWidth: 'auto', p: 0.5, flexShrink: 0 }}
                                                >
                                                    ✏️
                                                </Button>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontSize: '0.8rem', 
                                            color: amDesignColors.primary, 
                                            maxWidth: 150, 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis',
                                            fontWeight: 500
                                        }}>
                                            {business.phoneNumber && business.phoneNumber !== 'N/A' ? business.phoneNumber : '-'}
                                        </TableCell>
                                       
                                        <TableCell sx={{
                                            fontSize: '0.8rem',
                                            color: amDesignColors.primary,
                                            maxWidth: 280,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {business.websiteUrl && business.websiteUrl !== 'N/A' ? (
                                                <a
                                                    href={business.websiteUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: amDesignColors.primary,
                                                        textDecoration: 'none',
                                                        fontWeight: '500',
                                                        borderBottom: '1px dashed amDesignColors.primary',
                                                        paddingBottom: '1px'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.color = '#1565c0';
                                                        e.target.style.borderBottom = '1px solid #1565c0';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.color = amDesignColors.primary;
                                                        e.target.style.borderBottom = '1px dashed amDesignColors.primary';
                                                    }}
                                                >
                                                    {business.websiteUrl.length > 25 ? business.websiteUrl.substring(0, 25) + '...' : business.websiteUrl}
                                                </a>
                                            ) : '-'}
                                        </TableCell>

                                        

                                        <TableCell sx={{
                                            fontSize: '0.78rem',
                                            color: amDesignColors.text.secondary,
                                            fontFamily: "'JetBrains Mono', monospace",
                                            maxWidth: 'none',
                                            overflow: 'visible',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {editingCloudTenantId === business.locationId ? (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <TextField
                                                        size="small"
                                                        value={editCloudTenantValue}
                                                        onChange={(e) => setEditCloudTenantValue(e.target.value)}
                                                        placeholder="Cloud Tenant ID eingeben"
                                                        sx={{ width: '200px' }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="success"
                                                        onClick={() => handleCloudTenantSave(business.locationId, business.businessName)}
                                                    >
                                                        Speichern
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={handleCloudTenantCancel}
                                                    >
                                                        Abbrechen
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                    {business.contactCloudTenantId && business.contactCloudTenantId !== 'N/A' ? (
                                                        <span style={{ color: amDesignColors.text.secondary, fontFamily: "'JetBrains Mono', monospace" }}>
                                                            {business.contactCloudTenantId}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#999' }}>-</span>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={() => handleCloudTenantEdit(business.locationId, business.contactCloudTenantId)}
                                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                                    >
                                                        ✏️
                                                    </Button>
                                                </Box>
                                            )}
                                        </TableCell>

                                        <TableCell sx={{
                                            fontSize: '0.78rem',
                                            color: amDesignColors.text.secondary,
                                            fontFamily: "'JetBrains Mono', monospace",
                                            maxWidth: 160,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {business.userId}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            fontSize: '0.78rem', 
                                            color: amDesignColors.text.secondary, 
                                            fontFamily: "'JetBrains Mono', monospace", 
                                            maxWidth: 160, 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis' 
                                        }}>
                                            {business.teamId}
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            fontSize: '0.8rem',
                                            color: amDesignColors.text.secondary,
                                            fontStyle: 'italic'
                                        }}>
                                            {formatDate(business.createdAt)}
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            fontSize: '0.8rem',
                                            fontWeight: 500
                                        }}>
                                            <Chip
                                                label={business.reportSent ? 'Ja' : 'Nein'}
                                                color={business.reportSent ? 'success' : 'default'}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        </TableCell>

                                        {/* QR Code Column */}
                                        <TableCell align="center" sx={{
                                            fontSize: '0.8rem',
                                            fontWeight: 500
                                        }}>
                                            {(business.qrCodeUrl && business.qrCodeUrl.trim()) ? (
                                                // QR Code exists - show QR icon
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        console.log('🔍 QR Click:', business.businessName);
                                                        setQrModal({ 
                                                            open: true, 
                                                            business: {
                                                                ...business,
                                                                qrCodeUrl: business.qrCodeUrl,
                                                                realPlaceId: business.realPlaceId || business.placeId
                                                            }
                                                        });
                                                    }}
                                                    sx={{ color: amDesignColors.primary }}
                                                    title="QR Code anzeigen"
                                                >
                                                    <QrCodeIcon />
                                                </IconButton>
                                            ) : (
                                                // QR Code missing - show Create button
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={creatingQr === business.locationId}
                                                    onClick={() => createQrCode(business)}
                                                    sx={{ 
                                                        minWidth: '70px',
                                                        fontSize: '0.7rem',
                                                        py: 0.5
                                                    }}
                                                >
                                                    {creatingQr === business.locationId ? (
                                                        <CircularProgress size={14} />
                                                    ) : (
                                                        'Erstellen'
                                                    )}
                                                </Button>
                                            )}
                                        </TableCell>

                                        {/* Custom Prompt Column */}
                                        <TableCell align="center" sx={{
                                            fontSize: '0.8rem',
                                            fontWeight: 500
                                        }}>
                                            {business.customAiPrompt && business.customAiPrompt.trim() ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setCustomPromptModal({ open: true, business })}
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        py: 0.5,
                                                        borderColor: '#6366f1',
                                                        color: '#6366f1',
                                                        '&:hover': {
                                                            backgroundColor: '#f0f4ff',
                                                            borderColor: '#4f46e5'
                                                        }
                                                    }}
                                                >
                                                    Ansehen
                                                </Button>
                                            ) : (
                                                <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>
                                                    Keine
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>

                {/* Pagination */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mt: 4, 
                    mb: 2 
                }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        siblingCount={2}
                        boundaryCount={1}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                borderRadius: 2,
                                fontWeight: 'bold',
                                '&.Mui-selected': {
                                    background: 'linear-gradient(45deg, amDesignColors.primary 30%, #42a5f5 90%)',
                                    color: amDesignColors.white,
                                    boxShadow: '0 2px 10px rgba(25, 118, 210, 0.3)'
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                }
                            }
                        }}
                    />
                </Box>
            </>
        )}

        {/* Initial state - before any load attempt */}
        {!loading && businessesData.length === 0 && !error && !hasAttemptedLoad && (
            <Card sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  background: 'primary',
              
            }}>
                <Box sx={{ fontSize: '4rem', mb: 2 }}>📊</Box>
                <Typography variant="h5" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Unternehmensdaten laden
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                    Klicken Sie auf "Aktualisieren", um die Unternehmensdaten zu laden
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => loadAllBusinesses(true)}
                    startIcon={<Refresh />}
                    sx={{

                        background: 'primary',
                                color: 'primary',
                        fontWeight: 'bold',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    Daten laden
                </Button>
            </Card>
        )}

        {/* Empty state - after load attempt but no data */}
        {!loading && businessesData.length === 0 && !error && hasAttemptedLoad && (
            <Card sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, amDesignColors.paper 0%, #f5f5f5 100%)',
                border: '2px dashed amDesignColors.border'
            }}>
                <Box sx={{ fontSize: '4rem', mb: 2, opacity: 0.5 }}>🏢</Box>
                <Typography variant="h5" color="textSecondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Keine Unternehmen gefunden
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                    Versuchen Sie es erneut oder überprüfen Sie Ihre Verbindung
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => loadAllBusinesses(true)}
                    startIcon={<Refresh />}
                    sx={{
                        background: 'linear-gradient(45deg, amDesignColors.primary 30%, #42a5f5 90%)',
                        color: amDesignColors.white,
                        fontWeight: 'bold',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    Daten laden
                </Button>
            </Card>
        )}

        {/* Address Edit Modal */}
        <Dialog
            open={editingAddressId !== null}
            onClose={handleAddressCancel}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                Adresse bearbeiten
            </DialogTitle>
            <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label="Straße"
                    value={editAddressData.street}
                    onChange={(e) => handleAddressDataChange('street', e.target.value)}
                    fullWidth
                    size="small"
                />
                <TextField
                    label="Stadt"
                    value={editAddressData.city}
                    onChange={(e) => handleAddressDataChange('city', e.target.value)}
                    fullWidth
                    size="small"
                />
                <TextField
                    label="Bundesland/Staat"
                    value={editAddressData.state}
                    onChange={(e) => handleAddressDataChange('state', e.target.value)}
                    fullWidth
                    size="small"
                />
                <TextField
                    label="Postleitzahl"
                    value={editAddressData.postalCode}
                    onChange={(e) => handleAddressDataChange('postalCode', e.target.value)}
                    fullWidth
                    size="small"
                />
                <TextField
                    label="Land"
                    value={editAddressData.country}
                    onChange={(e) => handleAddressDataChange('country', e.target.value)}
                    fullWidth
                    size="small"
                />
                <TextField
                    label="Vollständige Adresse"
                    value={editAddressData.formattedAddress}
                    onChange={(e) => handleAddressDataChange('formattedAddress', e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                    Koordinaten
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Breitengrad (Lat)"
                        value={editAddressData.lat}
                        onChange={(e) => handleAddressDataChange('lat', e.target.value)}
                        placeholder="z.B. 52.5200"
                        size="small"
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Längengrad (Lng)"
                        value={editAddressData.lng}
                        onChange={(e) => handleAddressDataChange('lng', e.target.value)}
                        placeholder="z.B. 13.4050"
                        size="small"
                        sx={{ flex: 1 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={handleAddressCancel}
                    variant="outlined"
                >
                    Abbrechen
                </Button>
                <Button
                    onClick={() => {
                        const editingBusiness = businessesData.find(b => b.locationId === editingAddressId);
                        if (editingBusiness) {
                            handleAddressSave(editingAddressId, editingBusiness.businessName);
                        }
                    }}
                    variant="contained"
                    color="primary"
                >
                    Weiter zur Bestätigung
                </Button>
            </DialogActions>
        </Dialog>

        {/* Confirmation Dialog for Email, Cloud Tenant ID, and Address */}
        <Dialog
            open={confirmDialog}
            onClose={handleEmailConfirmCancel}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                {pendingSave?.fieldType === 'cloudTenantId' ? 'Cloud Tenant ID ändern bestätigen' : pendingSave?.fieldType === 'address' ? 'Adresse ändern bestätigen' : 'E-Mail ändern bestätigen'}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        <strong>Unternehmen:</strong> {pendingSave?.businessName}
                    </Typography>
                    {pendingSave?.fieldType === 'address' ? (
                        <>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Straße:</strong> {pendingSave?.address?.street || '-'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Stadt:</strong> {pendingSave?.address?.city || '-'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Bundesland:</strong> {pendingSave?.address?.state || '-'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Postleitzahl:</strong> {pendingSave?.address?.postalCode || '-'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Land:</strong> {pendingSave?.address?.country || '-'}
                            </Typography>
                            {pendingSave?.address?.lat && pendingSave?.address?.lng && (
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                    <strong>Koordinaten:</strong> {pendingSave?.address?.lat}, {pendingSave?.address?.lng}
                                </Typography>
                            )}
                        </>
                    ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            <strong>{pendingSave?.fieldType === 'cloudTenantId' ? 'Neue Cloud Tenant ID:' : 'Neue E-Mail:'}</strong> {pendingSave?.fieldType === 'cloudTenantId' ? pendingSave?.value : pendingSave?.email}
                        </Typography>
                    )}
                </Box>
                <Typography variant="body1" sx={{ color: '#d32f2f', fontWeight: 500 }}>
                    {pendingSave?.fieldType === 'cloudTenantId' ? 'Sind Sie sicher, dass Sie die Cloud Tenant ID ändern möchten?' : pendingSave?.fieldType === 'address' ? 'Sind Sie sicher, dass Sie die Adresse ändern möchten?' : 'Sind Sie sicher, dass Sie die E-Mail ändern möchten?'}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    onClick={handleEmailConfirmCancel}
                    variant="outlined"
                    sx={{ mr: 1 }}
                >
                    Abbrechen
                </Button>
                <Button
                    onClick={() => {
                        if (pendingSave?.fieldType === 'address') {
                            handleAddressConfirm();
                        } else {
                            handleEmailConfirm();
                        }
                    }}
                    variant="contained"
                    color="success"
                    sx={{
                        background: 'linear-gradient(45deg, #2e7d32 30%, #43a047 90%)',
                        fontWeight: 'bold'
                    }}
                >
                    Ja, bestätigen
                </Button>
            </DialogActions>
        </Dialog>

        {/* QR Code Modal */}
        <Dialog
            open={qrModal.open}
            onClose={() => setQrModal(prev => ({ ...prev, open: false }))}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', textAlign: 'center' }}>
                QR Code für Bewertungen
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
                {qrModal.business && (
                    <>
                        <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center', mb: 1 }}>
                            {qrModal.business.businessName}
                        </Typography>
                        
                        {/* QR Code Image */}
                        <Box sx={{ 
                            border: '2px solid amDesignColors.border', 
                            borderRadius: 2, 
                            p: 2, 
                            backgroundColor: amDesignColors.white,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <img 
                                src={qrModal.business.qrCodeUrl} 
                                alt="QR Code" 
                                style={{ 
                                    width: '200px', 
                                    height: '200px',
                                    display: 'block'
                                }}
                            />
                        </Box>

                        {/* Review URL */}
                        <Box sx={{ width: '100%', mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Bewertungs-URL:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={`https://search.google.com/local/writereview?placeid=${qrModal.business.realPlaceId || qrModal.business.placeId}`}
                                    InputProps={{
                                        readOnly: true,
                                        sx: { fontSize: '0.875rem' }
                                    }}
                                />
                                <IconButton 
                                    onClick={() => {
                                        navigator.clipboard.writeText(`https://search.google.com/local/writereview?placeid=${qrModal.business.realPlaceId || qrModal.business.placeId}`);
                                        setCopySuccess(true);
                                    }}
                                    color="primary"
                                    title="URL kopieren"
                                >
                                    <CopyIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button 
                    onClick={() => setQrModal(prev => ({ ...prev, open: false }))}
                    variant="outlined"
                >
                    Schließen
                </Button>
            </DialogActions>
        </Dialog>

        {/* Custom Prompt Modal */}
        <Dialog
            open={customPromptModal.open}
            onClose={() => setCustomPromptModal(prev => ({ ...prev, open: false }))}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                🤖 Zusätzliche KI-Anweisungen
            </DialogTitle>
            <DialogContent sx={{ py: 3 }}>
                {customPromptModal.business && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: amDesignColors.text.secondary }}>
                            {customPromptModal.business.businessName}
                        </Typography>

                        {/* Custom Prompt Content */}
                        <Box sx={{
                            backgroundColor: '#f8f9ff',
                            border: '1px solid #e0e7ff',
                            borderRadius: 1,
                            p: 2,
                            minHeight: '100px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: '0.95rem',
                            color: '#333',
                            fontFamily: "'JetBrains Mono', monospace"
                        }}>
                            {customPromptModal.business.customAiPrompt && customPromptModal.business.customAiPrompt.trim()
                                ? customPromptModal.business.customAiPrompt
                                : 'Keine KI-Anweisungen vorhanden'}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                    onClick={() => setCustomPromptModal(prev => ({ ...prev, open: false }))}
                    variant="outlined"
                >
                    Schließen
                </Button>
            </DialogActions>
        </Dialog>

        {/* Copy Success Notification */}
        <Snackbar
            open={copySuccess}
            autoHideDuration={2000}
            onClose={() => setCopySuccess(false)}
            message="URL in Zwischenablage kopiert!"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
    </Box>
);
};

export default AllBusinessesPage;
